<!--
  Note: This isn't an actual slider - Naming might be confusing - Used in eg: Section.vue
-->
<template>
  <div class="section__background-item" :style="videoSectionBackground">
    <video
      v-if="video?.type === 'external' && isReady"
      ref="externalVideo"
      playsinline
      autoplay
      muted
      :controls="false"
      :loop="!video.disableLooping"
    >
      <source :src="video.url" type="video/mp4" />
    </video>

    <div
      v-if="video?.url && video?.type !== 'external'"
      class="video-embed-wrap"
      :class="{
        'video-embed-wrap--not-ratio': !ratio
      }"
      :style="{ width: videoBackgroundStyles?.width }"
    >
      <div ref="videoPlayerRef" class="video-embed-element" :style="{ width: sectionBackgroundStyles?.width }"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, CSSProperties, defineComponent, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { VideoState } from '@/components/layout/SettingsModel';
import { BehaviorTypes, VideoFormatType } from '@/typings/enums/enums';
import YouTubePlayer from 'youtube-player';
// @ts-ignore
import VimeoPlayer from '@vimeo/player';
import { applyReplacementTags, getVimeoIdFromUrl, getYouTubeVideoIdFromUrl } from '@/utilities/Utilities';
import PlayerStates from 'youtube-player/dist/constants/PlayerStates';

interface StateChangeEvent {
  data: number;
}

interface YoutubeCustomEvent extends CustomEvent {
  target: YoutubeEvent;
}

interface YoutubeVideoData {
  video_id?: string;
  author: string;
  title: string;
}

interface YoutubeEvent extends EventTarget {
  /**
   * We just type what we need from the api - api is full documented:
   * @see https://developers.google.com/youtube/iframe_api_reference
   * */
  getVideoData(): YoutubeVideoData;
}

export default defineComponent({
  name: 'BackgroundVideoSlider',
  props: {
    sectionBackgroundStyles: {
      type: Object as PropType<CSSProperties>
    },
    videoBackgroundStyles: {
      type: Object as PropType<CSSProperties>
    },
    video: {
      type: Object as PropType<VideoState>
    },
    ratio: {
      type: Boolean
    },
    videoWidth: {
      type: [String, Number]
    }
  },
  setup(props) {
    const videoPlayerRef = ref<HTMLElement | null>(null);
    const externalVideo = ref<HTMLMediaElement | null>(null);
    let player: typeof YouTubePlayer | VimeoPlayer | null = null;

    const src = computed(() => {
      return applyReplacementTags(props.video?.url ?? '');
    });

    const youtubePlayer = () => {
      if (videoPlayerRef.value instanceof HTMLElement && videoPlayerRef.value && src.value) {
        player = YouTubePlayer(videoPlayerRef.value, {
          videoId: getYouTubeVideoIdFromUrl(src.value),
          playerVars: {
            origin: window.location.origin,
            widget_referrer: window.location.origin,
            rel: 0,
            controls: 0,
            modestbranding: 1,
            playsinline: 1
          }
        });
        player.on('ready', (event: YoutubeCustomEvent) => {
          player.setVolume(0);

          if (event?.target?.getVideoData().video_id) {
            player.playVideo();
          } else {
            player.destroy();
          }
        });
        player.on('stateChange', (event: StateChangeEvent) => {
          if (!props.video?.disableLooping && event.data === PlayerStates.ENDED) {
            player.playVideo();
          }
        });
      }
    };

    const vimeoPlayer = () => {
      if (src.value) {
        player = new VimeoPlayer(videoPlayerRef.value, {
          id: getVimeoIdFromUrl(src.value),
          loop: !props.video?.disableLooping,
          autoplay: 1,
          sound: 0,
          controls: 0,
          background: 1
        });
      }
    };

    const externalPlayer = async () => {
      isReady.value = true;
    };

    const videoSectionBackground = computed(() => {
      const position = props.video?.fallbackImage?.position?.replace('_', ' ');

      let backgroundBehavior: string;

      switch (props.video?.fallbackImage?.behavior) {
        case BehaviorTypes.CONTAIN:
          backgroundBehavior = 'contain';
          break;
        case BehaviorTypes.STRETCH:
          backgroundBehavior = 'stretch';
          break;
        case BehaviorTypes.TILE:
          backgroundBehavior = 'tile';
          break;
        case BehaviorTypes.ACTUAL_SIZE:
          backgroundBehavior = 'actual_size';
          break;
        default:
          backgroundBehavior = 'cover';
          break;
      }

      return {
        ...props.sectionBackgroundStyles,
        ...(props.video?.fallbackImage?.enabled && {
          ...(props.video?.fallbackImage.url && {
            backgroundImage: `url(${props.video?.fallbackImage.url})`
          }),
          ...(position && { backgroundPosition: position }),
          backgroundSize: backgroundBehavior
        })
      };
    });

    const initVideoEmbed = () => {
      if (props.video?.type !== undefined) {
        switch (props.video.type) {
          case VideoFormatType.EXTERNAL:
            externalPlayer();
            break;

          case VideoFormatType.YOUTUBE:
            youtubePlayer();
            break;

          case VideoFormatType.VIMEO:
            vimeoPlayer();
            break;
        }
      }
    };

    const isReady = ref(false);

    const reload = () => {
      if (!src.value || player !== null || player?.destroy) {
        if (player) {
          player.destroy();
          player = null;
        }
      }
      nextTick(() => {
        if (src.value && !player) {
          initVideoEmbed();
        }
      });
    };

    watch(src, () => {
      if (!src.value) {
        isReady.value = false;
      }
      reload();
    });

    watch(
      () => props.video?.disableLooping,
      () => {
        isReady.value = false;
        reload();
      }
    );

    onMounted(() => {
      initVideoEmbed();
    });

    //Video
    return {
      videoPlayerRef,
      VideoFormatType,
      videoSectionBackground,
      isReady,
      externalVideo
    };
  }
});
</script>
